/**
 * Chatify E2E Encryption — RSA-OAEP + AES-GCM hybrid
 * Private key NEVER leaves the browser (stored in localStorage)
 */

const PRIVATE_KEY_STORE = "chatify_private_key";
const PUBLIC_KEY_STORE  = "chatify_public_key_jwk";

// ── Helpers ──────────────────────────────────────────────────────────────────

const toB64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let str = "";
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str);
};

const fromB64 = (b64) =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

// ── Key pair generation (called once on login/register) ───────────────────────

export const generateKeyPair = async () => {
  const pair = await crypto.subtle.generateKey(
    { name: "RSA-OAEP", modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256" },
    true, ["encrypt", "decrypt"]
  );
  const pubJwk  = await crypto.subtle.exportKey("jwk", pair.publicKey);
  const privJwk = await crypto.subtle.exportKey("jwk", pair.privateKey);
  localStorage.setItem(PRIVATE_KEY_STORE, JSON.stringify(privJwk));
  localStorage.setItem(PUBLIC_KEY_STORE,  JSON.stringify(pubJwk));
  return { publicKeyJwk: pubJwk };
};

export const hasPrivateKey = () => !!localStorage.getItem(PRIVATE_KEY_STORE);

export const getPublicKeyJwk = () => {
  const s = localStorage.getItem(PUBLIC_KEY_STORE);
  return s ? JSON.parse(s) : null;
};

const loadPrivateKey = async () => {
  const s = localStorage.getItem(PRIVATE_KEY_STORE);
  if (!s) return null;
  return crypto.subtle.importKey(
    "jwk", JSON.parse(s),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false, ["decrypt"]
  );
};

const loadPublicKey = (jwk) =>
  crypto.subtle.importKey(
    "jwk", typeof jwk === "string" ? JSON.parse(jwk) : jwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false, ["encrypt"]
  );

// ── Encrypt message for a list of recipients ─────────────────────────────────
// Each recipient gets their own RSA-encrypted copy of the AES key
// recipients = [{ _id: string, publicKey: jwk }]

export const encryptMessage = async (plaintext, recipients) => {
  if (!plaintext || !recipients?.length) return null;

  // 1. Generate one-time AES-256-GCM key
  const aesKey = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]
  );
  const aesRaw = await crypto.subtle.exportKey("raw", aesKey);

  // 2. Encrypt plaintext
  const iv  = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const ct  = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, enc.encode(plaintext));

  // 3. Encrypt AES key for each recipient (including sender)
  const encryptedKeys = {};
  for (const r of recipients) {
    if (!r?.publicKey) continue;
    const id = (r._id || "").toString();
    if (!id) continue;
    try {
      const pub    = await loadPublicKey(r.publicKey);
      const encKey = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, pub, aesRaw);
      encryptedKeys[id] = toB64(encKey);
    } catch (e) {
      console.warn("Could not encrypt for", id, e.message);
    }
  }

  // Format: "E2E|ivB64|ciphertextB64"
  return {
    ciphertext:    `E2E|${toB64(iv)}|${toB64(ct)}`,
    encryptedKeys,
    encrypted:     true,
  };
};

// ── Decrypt a message ─────────────────────────────────────────────────────────

export const decryptMessage = async (ciphertext, encryptedKeyB64) => {
  try {
    if (!ciphertext || !encryptedKeyB64) return null;

    // Must start with E2E| prefix — reject old broken format
    if (!ciphertext.startsWith("E2E|")) return null;

    const parts = ciphertext.split("|");
    if (parts.length !== 3) return null;
    const [, ivB64, ctB64] = parts;

    const privateKey = await loadPrivateKey();
    if (!privateKey) return null;

    // Decrypt AES key
    const aesRaw = await crypto.subtle.decrypt(
      { name: "RSA-OAEP" }, privateKey, fromB64(encryptedKeyB64)
    );
    const aesKey = await crypto.subtle.importKey(
      "raw", aesRaw, { name: "AES-GCM" }, false, ["decrypt"]
    );

    // Decrypt message
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromB64(ivB64) }, aesKey, fromB64(ctB64)
    );

    return new TextDecoder().decode(plain);
  } catch (e) {
    console.warn("decryptMessage failed:", e.message);
    return null;
  }
};

// ── Clear keys on logout ──────────────────────────────────────────────────────

export const clearKeys = () => {
  localStorage.removeItem(PRIVATE_KEY_STORE);
  localStorage.removeItem(PUBLIC_KEY_STORE);
};
